import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, FileText, Map as MapIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import BaReviewSection from "@/components/BaReviewSection";

const PHASE_META = {
  DPS:               { label: "DPS",           color: "bg-slate-300",   text: "text-slate-800",  short: "DPS"  },
  BA_UPLOADED:       { label: "BA DPSHP ↑",    color: "bg-amber-400",   text: "text-amber-900",  short: "↑D"  },
  DPSHP:             { label: "DPSHP 🔒",      color: "bg-emerald-600", text: "text-white",      short: "DPSHP" },
  DPT_BA_UPLOADED:   { label: "BA DPT ↑",      color: "bg-sky-400",     text: "text-sky-900",    short: "↑T"  },
  DPT:               { label: "DPT 🔒",        color: "bg-blue-800",    text: "text-white",      short: "DPT"  },
};

export default function LockProgressPage() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tpsList, setTpsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDusun, setFilterDusun] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/tps-phase-summary"),
        api.get("/tps?include_progress=true"),
      ]);
      setSummary(s.data);
      setTpsList(t.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!summary) {
    return <div className="text-sm text-slate-500 p-6">{loading ? "Memuat..." : "Tidak ada data"}</div>;
  }

  const filteredTps = filterDusun === "all" ? tpsList : tpsList.filter((t) => t.dusun === filterDusun);

  return (
    <div className="space-y-6" data-testid="lock-progress-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Status Penguncian</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Progres Lock DPS → DPSHP → DPT</h1>
        <p className="text-slate-600 text-sm mt-2">Ringkasan visual seluruh TPS desa, per fase penguncian.</p>
      </header>

      {/* Header KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lock-kpis">
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total TPS</div>
          <div className="text-3xl font-bold tabular-nums">{summary.total_tps}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Terkunci (DPSHP+)</div>
          <div className="text-3xl font-bold tabular-nums text-emerald-700">{summary.locked_percent}%</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">DPT Final</div>
          <div className="text-3xl font-bold tabular-nums text-blue-900">{summary.dpt_percent}%</div>
        </CardContent></Card>
        <Card className="cursor-pointer" onClick={load}><CardContent className="pt-5 flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-slate-500" />
          <div className="text-sm text-slate-600">Klik untuk muat ulang</div>
        </CardContent></Card>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(PHASE_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1">
            <span className={`w-3 h-3 rounded ${m.color}`} />
            <span className="font-medium">{m.label}</span>
            <span className="text-slate-500">({summary.by_phase[k] || 0})</span>
          </div>
        ))}
      </div>

      {/* By Dusun progress bars */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Progres per Dusun</p>
          </div>
          {summary.by_dusun.map((d) => (
            <div key={d.dusun} data-testid={`dusun-row-${d.dusun}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <div className="font-semibold">{d.dusun} <span className="text-xs text-slate-500 font-normal">({d.total} TPS)</span></div>
                <div className="text-xs tabular-nums">{d.locked}/{d.total} terkunci · <b className="text-emerald-700">{d.percent_locked}%</b></div>
              </div>
              <div className="h-3 w-full rounded bg-slate-100 overflow-hidden flex">
                {Object.entries(PHASE_META).map(([k, m]) => {
                  const cnt = d[k] || 0;
                  if (!cnt) return null;
                  const pct = (cnt / d.total) * 100;
                  return <div key={k} className={m.color} style={{ width: `${pct}%` }} title={`${m.label}: ${cnt}`} />;
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Filter + Grid TPS */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Grid TPS</p>
            <div className="flex gap-1 flex-wrap">
              <Button size="sm" variant={filterDusun === "all" ? "default" : "outline"} onClick={() => setFilterDusun("all")} data-testid="filter-dusun-all">Semua</Button>
              {summary.by_dusun.map((d) => (
                <Button key={d.dusun} size="sm" variant={filterDusun === d.dusun ? "default" : "outline"} onClick={() => setFilterDusun(d.dusun)} data-testid={`filter-dusun-${d.dusun}`}>{d.dusun}</Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2" data-testid="tps-grid">
            {filteredTps.map((t) => {
              const m = PHASE_META[t.phase] || PHASE_META.DPS;
              const p = t.progress || {};
              return (
                <button
                  key={t.id}
                  onClick={() => nav("/app/wilayah")}
                  className={`group relative ${m.color} ${m.text} p-2 rounded text-left hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition`}
                  title={`${t.kode} · ${t.dusun || ""} · ${m.label} · ${p.coklit_verified || 0}/${p.active_voters || 0} (${p.coklit_progress_pct || 0}%)`}
                  data-testid={`tps-cell-${t.kode}`}
                >
                  <div className="font-mono text-[10px] font-bold opacity-80">{t.kode}</div>
                  <div className="text-[10px] mt-0.5">{m.short}</div>
                  <div className="text-[9px] opacity-75 mt-0.5">{p.coklit_progress_pct || 0}%</div>
                </button>
              );
            })}
          </div>
          {filteredTps.length === 0 && (
            <p className="text-sm text-slate-500 italic text-center py-6">Tidak ada TPS untuk filter ini.</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-5 text-sm text-slate-700">
          <p className="flex items-center gap-2 font-semibold text-amber-900"><FileText className="h-4 w-4" /> Aksi cepat</p>
          <p className="mt-1">Untuk <b>force lock</b>, <b>unlock</b>, atau unggah BA per TPS, buka halaman <button onClick={() => nav("/app/wilayah")} className="underline font-semibold text-amber-900" data-testid="goto-wilayah">Wilayah → tab TPS</button>. Gunakan checkbox baris untuk bulk action.</p>
        </CardContent>
      </Card>

      {/* Review BA (dari PPDP) — untuk naikkan phase */}
      <BaReviewSection />
    </div>
  );
}
